"use client";

import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Cpu,
  Download,
  Loader2,
  MapPin,
  Plus,
  RefreshCw,
  SlidersHorizontal,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import type { DeviceAssignedUserSuggestionDto, DeviceDto, DeviceLocationSuggestionDto } from "@/lib/api/types";
import type { JsonObject } from "@/lib/api/types/json";
import { listUsers } from "@/lib/api/endpoints/users";
import { listTenants } from "@/lib/api/endpoints/tenants";
import type { UserDto } from "@/lib/api/types/enterprise";
import { ListCount, ListSurface, ListSurfaceBody, ListSurfaceFooter, ListSurfaceHeader, ListToolbar, ListToolbarGroup, ToolbarIconButton } from "@/components/ds/list-page";
import { EmptyState } from "@/components/ds/empty-state";
import { Modal } from "@/components/ds/modal";
import { backupAndDeleteDevice, backupDevice } from "@/lib/api/endpoints/data-management";
import { Button } from "@/components/ds/button";
import { WorkspacePage } from "@/components/ds/workspace-page";
import { FlashToast } from "@/components/ui-system/flash-toast";
import { Alert, Card, SkeletonTableRow } from "@/components/ui-system";
import { ThemedSelect, TableHeaderSearch, tableHeaderSelectTriggerClass } from "@/components/ui-system/select";
import { useOverlayPosition } from "@/components/ui-system/use-overlay-position";
import { listDevicesPage } from "@/lib/api/endpoints/devices";
import { listDeviceCategoryFilterOptions } from "@/lib/api/endpoints/device-categories";
import {
  formatDeviceLastSeen,
  getDeviceAssignedUsers,
  getDeviceLifecycleDates,
  getDeviceLocationLabel,
  getDeviceProtocolLabel,
  getDeviceSamplingLabel,
  getDeviceSecondaryIdentifier,
  getDeviceTags,
} from "@/lib/devices/device-field-helpers";
import { deviceActivityModelFromApi, formatDeviceOperationalStatusLabel } from "@/lib/devices/device-state-model";
import { cn } from "@/lib/utils/cn";
import { DeviceDisableModal } from "@/modules/devices/components/device-disable-modal";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { isSuperAdmin as userIsSuperAdmin } from "@/modules/auth/permissions";
import { useAuthStore } from "@/modules/auth/store";
import {
  DEVICE_MANAGEMENT_DEFAULT_PAGE_SIZE,
  DEVICE_MANAGEMENT_PAGE_SIZE_OPTIONS,
  useDeleteDeviceMutation,
  useDeviceAssignedUserSuggestions,
  useDeviceLocationSuggestions,
  useDeviceManagementList,
  usePatchDeviceMutation,
  useUpdateDeviceAssignmentsMutation,
  type DeviceManagementListFilters,
} from "@/modules/devices/hooks";
import { readDeviceDisableState } from "@/lib/devices/device-disable-state";
import { DeviceTable, type DeviceTableSortKey } from "@/modules/devices/components/device-table";
import { DEVICE_TABLE_DEFAULT_SORT_ORDER } from "@/modules/devices/components/device-table-columns";

const STATUS_OPTIONS = [
  { value: "", label: "Any state" },
  { value: "online", label: "Online" },
  { value: "stale", label: "Stale" },
  { value: "offline", label: "Offline" },
  { value: "error", label: "Error" },
  { value: "maintenance", label: "Maintenance" },
];

const PROTOCOL_OPTIONS = [
  { value: "", label: "Any protocol" },
  { value: "mqtt", label: "MQTT" },
  { value: "modbus", label: "Modbus" },
  { value: "http", label: "HTTP" },
];

const SIM_TYPE_OPTIONS = [
  { value: "", label: "Any sim type" },
  { value: "phone", label: "Phone" },
  { value: "industrial", label: "Industrial" },
  { value: "other", label: "Other" },
];

const ASSIGNMENT_OPTIONS = [
  { value: "", label: "Any assignment" },
  { value: "assigned", label: "Assigned" },
  { value: "unassigned", label: "Unassigned" },
];

const WARRANTY_OPTIONS = [
  { value: "", label: "Any warranty" },
  { value: "expiring_this_month", label: "Expiring this month" },
  { value: "next_30d", label: "Due in 30 days" },
  { value: "next_90d", label: "Due in 90 days" },
  { value: "expired", label: "Expired" },
  { value: "unknown", label: "Unknown" },
];

const ALERT_PRESET_OPTIONS = [
  { value: "", label: "Any alerts" },
  { value: "threshold_breached", label: "Threshold breached" },
];

const ALERT_RANGE_OPTIONS = [
  { value: "last_24h", label: "Last 24h" },
  { value: "last_7d", label: "Last 7d" },
  { value: "custom", label: "Custom" },
];

function inputClass() {
  return cn(
    "h-10 w-full min-w-0 rounded-lg border border-border bg-card px-ds-3 text-caption text-foreground shadow-sm",
    "placeholder:text-muted hover:border-primary/20 focus:border-primary focus:outline-none focus:ring-0",
  );
}

function filterTriggerClass(applied: boolean) {
  return cn(
    tableHeaderSelectTriggerClass,
    applied && "!border-[var(--accent-primary)] !bg-[var(--accent-primary-muted)] !text-[var(--accent-primary)]",
  );
}

function locationKindLabel(kind: DeviceLocationSuggestionDto["kind"] | ""): string {
  if (kind === "site") return "Site / asset context";
  if (kind === "city") return "City";
  if (kind === "state") return "State";
  return "Location";
}

const DEVICE_EXPORT_HEADERS = [
  "Logger",
  "UID",
  "Tags",
  "Health",
  "Last Seen",
  "Protocol",
  "Sampling",
  "Period",
  "Install",
  "Warranty",
  "Calibration",
  "Location",
  "Region",
  "Assigned",
  "More assignees",
] as const;

function exportCell(value: string | number | null | undefined): string {
  const text = String(value ?? "");
  const safeText = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return `"${safeText.replace(/"/g, '""')}"`;
}

function lifecycleExportValue(device: DeviceDto, label: string): string {
  return getDeviceLifecycleDates(device).find((entry) => entry.label === label)?.value ?? "—";
}

function deviceExportRow(device: DeviceDto): string[] {
  const activity = deviceActivityModelFromApi(device);
  const tags = getDeviceTags(device);
  const sampling = getDeviceSamplingLabel(device);
  const location = getDeviceLocationLabel(device);
  const assignedUsers = getDeviceAssignedUsers(device);

  return [
    device.name?.trim() || device.device_uid,
    getDeviceSecondaryIdentifier(device),
    tags.join(", ") || "—",
    formatDeviceOperationalStatusLabel(activity.status),
    formatDeviceLastSeen(device.activity_last_seen ?? device.last_seen),
    getDeviceProtocolLabel(device),
    sampling.primary,
    sampling.secondary,
    lifecycleExportValue(device, "Install"),
    lifecycleExportValue(device, "Warranty"),
    lifecycleExportValue(device, "Calibration"),
    location.primary,
    location.secondary || "—",
    assignedUsers[0] ?? "Unassigned",
    assignedUsers.length > 1 ? `+${assignedUsers.length - 1} more` : "—",
  ];
}

function downloadDeviceCsv(devices: DeviceDto[]): void {
  const csv = [DEVICE_EXPORT_HEADERS, ...devices.map(deviceExportRow)]
    .map((row) => row.map(exportCell).join(","))
    .join("\r\n");
  const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `data-loggers-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function LocationFilterInput({
  value,
  selectedKind,
  tenantId,
  workspaceScope,
  active = false,
  onInputChange,
  onSelect,
}: {
  value: string;
  selectedKind: DeviceManagementListFilters["locationKind"];
  tenantId: string | null;
  workspaceScope: "all" | null;
  active?: boolean;
  onInputChange: (value: string) => void;
  onSelect: (option: DeviceLocationSuggestionDto) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const position = useOverlayPosition(open, rootRef, "bottom-start", "anchor");
  const suggestionsQuery = useDeviceLocationSuggestions(value, tenantId, workspaceScope, open);
  const items = useMemo(
    () => suggestionsQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [suggestionsQuery.data],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleScroll = useCallback(() => {
    const node = listRef.current;
    if (!node || !suggestionsQuery.hasNextPage || suggestionsQuery.isFetchingNextPage) {
      return;
    }
    const remaining = node.scrollHeight - node.scrollTop - node.clientHeight;
    if (remaining < 40) {
      void suggestionsQuery.fetchNextPage();
    }
  }, [suggestionsQuery]);

  return (
    <div ref={rootRef} className="w-[10rem] shrink-0">
      <div className={cn("ui-control flex h-9 min-h-9 w-full items-center gap-2.5 px-3", active && "!border-[var(--accent-primary)] !bg-[var(--accent-primary-muted)]")}>
        <MapPin className={cn("pointer-events-none h-4 w-4 shrink-0 text-muted", active && "!text-[var(--accent-primary)]")} aria-hidden />
        <input
          type="search"
          value={value}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            onInputChange(event.target.value);
            setOpen(true);
          }}
          placeholder="Location"
          aria-label="Location"
          className="h-full w-full min-w-0 border-0 bg-transparent p-0 text-caption text-foreground outline-none placeholder:text-muted"
        />
      </div>
      {open && position && typeof document !== "undefined"
        ? createPortal(
          <div ref={menuRef} style={position} className="overflow-hidden rounded-2xl border border-card-border bg-card p-1.5 shadow-card">
          <div
            ref={listRef}
            onScroll={handleScroll}
            className="max-h-72 overflow-y-auto pr-1"
          >
            {items.map((option) => (
              <button
                key={`${option.kind}:${option.label.toLowerCase()}`}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onSelect(option);
                  setOpen(false);
                }}
                className="flex w-full min-w-0 items-center gap-2 rounded-xl px-3 py-2.5 text-left text-muted transition-colors hover:bg-muted-bg/35 hover:text-foreground"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-caption font-semibold text-foreground">{option.label}</span>
                  <span className="mt-1 block truncate text-micro text-muted/85">{locationKindLabel(option.kind)}</span>
                </span>
              </button>
            ))}
            {suggestionsQuery.isLoading ? (
              <div className="flex items-center justify-center gap-2 px-3 py-6 text-caption text-muted">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Loading locations...
              </div>
            ) : null}
            {!suggestionsQuery.isLoading && items.length === 0 ? (
              <div className="px-3 py-6 text-center text-caption text-muted">No locations found.</div>
            ) : null}
            {suggestionsQuery.isFetchingNextPage ? (
              <div className="flex items-center justify-center gap-2 px-3 py-3 text-caption text-muted">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Loading more...
              </div>
            ) : null}
            {!suggestionsQuery.hasNextPage && items.length > 0 ? (
              <div className="px-3 py-3 text-center text-micro text-muted">
                No more items
                {selectedKind ? ` · filtered by ${locationKindLabel(selectedKind)}` : ""}
              </div>
            ) : null}
          </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}

function AssignedUserFilterInput({
  value,
  selectedUserId,
  tenantId,
  workspaceScope,
  onInputChange,
  onSelect,
}: {
  value: string;
  selectedUserId: string;
  tenantId: string | null;
  workspaceScope: "all" | null;
  onInputChange: (value: string) => void;
  onSelect: (user: DeviceAssignedUserSuggestionDto) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const position = useOverlayPosition(open, rootRef, "bottom-start", "anchor");
  const suggestionsQuery = useDeviceAssignedUserSuggestions(value, tenantId, workspaceScope, open);
  const items = useMemo(
    () => suggestionsQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [suggestionsQuery.data],
  );

  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !menuRef.current?.contains(target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleScroll = useCallback(() => {
    const node = listRef.current;
    if (!node || !suggestionsQuery.hasNextPage || suggestionsQuery.isFetchingNextPage) return;
    if (node.scrollHeight - node.scrollTop - node.clientHeight < 40) void suggestionsQuery.fetchNextPage();
  }, [suggestionsQuery]);

  return (
    <div ref={rootRef} className="w-[12rem] shrink-0">
      <div className={cn("ui-control flex h-9 min-h-9 w-full items-center px-3", selectedUserId && "!border-[var(--accent-primary)] !bg-[var(--accent-primary-muted)]")}>
        <input
          type="search"
          value={value}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            onInputChange(event.target.value);
            setOpen(true);
          }}
          placeholder="Assigned to"
          aria-label="Assigned to user"
          className="h-full w-full min-w-0 border-0 bg-transparent p-0 text-caption text-foreground outline-none placeholder:text-muted"
        />
      </div>
      {open && position && typeof document !== "undefined"
        ? createPortal(
          <div ref={menuRef} style={position} className="w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-card-border bg-card p-1.5 shadow-card">
            <div ref={listRef} onScroll={handleScroll} className="max-h-72 overflow-y-auto pr-1">
              {items.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onSelect(user);
                    setOpen(false);
                  }}
                  className="flex w-full min-w-0 items-center rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-muted-bg/35"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-caption font-semibold text-foreground">{user.name?.trim() || user.email || "Unknown user"}</span>
                    <span className="mt-1 block truncate text-micro text-muted/85">
                      {user.email || "No email"}
                    </span>
                  </span>
                </button>
              ))}
              {suggestionsQuery.isLoading ? <div className="flex items-center justify-center gap-2 px-3 py-6 text-caption text-muted"><Loader2 className="h-4 w-4 animate-spin" aria-hidden />Loading users...</div> : null}
              {!suggestionsQuery.isLoading && items.length === 0 ? <div className="px-3 py-6 text-center text-caption text-muted">No assigned users found.</div> : null}
              {suggestionsQuery.isFetchingNextPage ? <div className="flex items-center justify-center gap-2 px-3 py-3 text-caption text-muted"><Loader2 className="h-4 w-4 animate-spin" aria-hidden />Loading more...</div> : null}
            </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}

function formatDateTimeLocalInput(value: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}`;
}

function isoToLocalDateTimeInput(value: string): string {
  if (!value) {
    return "";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return formatDateTimeLocalInput(parsed);
}

function localDateTimeToIso(value: string): string {
  if (!value) {
    return "";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toISOString();
}

function roleLabel(role: string): string {
  if (role === "super_admin") return "Super Admin";
  if (role === "admin") return "Admin";
  if (role === "distributor") return "Distributor";
  if (role === "user") return "User";
  return role;
}

function readAssignedUserIds(device: DeviceDto): string[] {
  if (device.assigned_users?.length) {
    return device.assigned_users.map((user) => user.id);
  }
  const meta = (device.metadata ?? {}) as JsonObject;
  const values = [
    meta.assigned_user_ids,
    meta.assignedUserIds,
    Array.isArray(meta.assigned_users)
      ? meta.assigned_users.map((item) =>
          item && typeof item === "object" && !Array.isArray(item) && typeof (item as JsonObject).id === "string"
            ? String((item as JsonObject).id)
            : "",
        )
      : null,
  ];

  for (const entry of values) {
    if (Array.isArray(entry)) {
      const ids = entry.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
      if (ids.length > 0) {
        return ids;
      }
    }
  }

  return [];
}

function filterAssignableUsers(currentUser: { role?: string | null } | null, users: UserDto[]): UserDto[] {
  const role = currentUser?.role ?? null;
  if (role === "admin") {
    return users.filter((user) => user.is_active && (user.role === "distributor" || user.role === "user"));
  }
  if (role === "distributor") {
    return users.filter((user) => user.is_active && user.role === "user");
  }
  if (role === "super_admin") {
    return users.filter((user) => user.is_active && user.role === "admin");
  }
  return [];
}

export function DeviceManagementPage() {
  const me = useAuthStore((s) => s.user);
  const tenantId = useAuthStore((s) => s.tenantId);
  const { permissionMap } = useEffectivePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const isPlatformOperator = userIsSuperAdmin(me) || me?.accountType === "platform";
  const isSuperAdmin = isPlatformOperator;
  const canEditDevices = hasModulePermission(me, permissionMap, "devices", "edit");
  const canCreateDevices = hasModulePermission(me, permissionMap, "devices", "create");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState<DeviceManagementListFilters>({
    search: "",
    status: "",
    statusScope: "",
    protocol: "",
    locationValue: "",
    locationKind: "",
    simType: "",
    assignmentState: "",
    assignedUserId: "",
    warrantyScope: "",
    deviceCategoryId: "",
    alertPreset: "",
    alertRangeKey: "",
    alertStart: "",
    alertEnd: "",
    sort: null,
    order: "desc",
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(DEVICE_MANAGEMENT_DEFAULT_PAGE_SIZE);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [confirmDelete, setConfirmDelete] = useState<null | { mode: "one" | "bulk"; device?: DeviceDto }>(null);
  const [confirmBackupDelete, setConfirmBackupDelete] = useState<null | DeviceDto>(null);
  const [disableDialog, setDisableDialog] = useState<null | DeviceDto>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [assignDialog, setAssignDialog] = useState<null | { devices: DeviceDto[]; mode: "single" | "bulk" }>(null);
  const [assignmentWorkspaceFilter, setAssignmentWorkspaceFilter] = useState<string>("");
  const [assignedUserIds, setAssignedUserIds] = useState<string[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [locationInput, setLocationInput] = useState("");
  const [assignedUserInput, setAssignedUserInput] = useState("");

  const effectiveWorkspaceFilter = isSuperAdmin ? (tenantId ?? "all") : (tenantId ?? "");
  const isAllWorkspacesScope = isSuperAdmin && !tenantId;

  useEffect(() => {
    setPage(0);
    setSelectedIds(new Set());
  }, [effectiveWorkspaceFilter]);

  const effectiveFilters = useMemo(
    () => ({
      ...filters,
      alertStart: filters.alertRangeKey === "custom" ? localDateTimeToIso(filters.alertStart) : filters.alertStart,
      alertEnd: filters.alertRangeKey === "custom" ? localDateTimeToIso(filters.alertEnd) : filters.alertEnd,
    }),
    [filters],
  );

  useEffect(() => {
    setLocationInput(filters.locationValue);
  }, [filters.locationValue]);

  const listQuery = useDeviceManagementList(
    effectiveFilters,
    page,
    pageSize,
    isAllWorkspacesScope ? null : (effectiveWorkspaceFilter || null),
    isAllWorkspacesScope ? "all" : null,
    isAllWorkspacesScope || Boolean(effectiveWorkspaceFilter),
  );
  const categoriesQuery = useQuery({
    queryKey: ["device-categories", "active"],
    queryFn: listDeviceCategoryFilterOptions,
    enabled: isPlatformOperator,
    staleTime: 60_000,
  });
  const tenantsQuery = useQuery({
    queryKey: ["tenants", "device-assignment"],
    queryFn: listTenants,
    enabled: isSuperAdmin,
    staleTime: 60_000,
  });
  const usersQuery = useQuery({
    queryKey: ["users", assignmentWorkspaceFilter || "all", "device-assign"],
    queryFn: () => (
      assignmentWorkspaceFilter
        ? listUsers(assignmentWorkspaceFilter)
        : listUsers(null, "all")
    ),
    enabled: assignDialog !== null && isSuperAdmin,
    staleTime: 60_000,
  });
  const deleteMut = useDeleteDeviceMutation();
  const patchMut = usePatchDeviceMutation();
  const assignmentMut = useUpdateDeviceAssignmentsMutation();

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const canManageCurrentScope = isSuperAdmin || !isAllWorkspacesScope;
  const hasAnyFilters = Boolean(
      filters.search ||
      filters.status ||
      filters.statusScope ||
      filters.protocol ||
      filters.locationValue ||
      filters.simType ||
      filters.assignmentState ||
      filters.assignedUserId ||
      filters.warrantyScope ||
      filters.deviceCategoryId ||
      filters.alertPreset ||
      filters.sort ||
      (isSuperAdmin && !isAllWorkspacesScope),
  );
  const hasAdvancedFilters = Boolean(
      filters.protocol ||
      filters.status ||
      filters.statusScope ||
      filters.locationValue ||
      filters.simType ||
      filters.assignmentState ||
      filters.assignedUserId ||
      filters.warrantyScope ||
      filters.deviceCategoryId ||
      filters.alertPreset ||
      filters.alertRangeKey ||
      filters.alertStart ||
      filters.alertEnd ||
      filters.sort ||
      filters.order !== "desc",
  );
  const assignableUsers = useMemo(
    () => filterAssignableUsers(me ?? null, usersQuery.data?.items ?? []),
    [me, usersQuery.data?.items],
  );
  const selectedVisibleDevices = useMemo(
    () => items.filter((device) => selectedIds.has(device.id)),
    [items, selectedIds],
  );
  const selectionFullyLoaded = selectedVisibleDevices.length === selectedIds.size;
  const bulkAssignableSelection = useMemo(
    () => selectionFullyLoaded && selectedVisibleDevices.length > 0 && selectedVisibleDevices.every((device) => readAssignedUserIds(device).length === 0),
    [selectedVisibleDevices, selectionFullyLoaded],
  );
  const bulkAssignmentLabel = selectedVisibleDevices.length === 1 ? "Assign selected" : `Assign ${selectedVisibleDevices.length} selected`;
  const assignedUserId = assignedUserIds[0] ?? "";
  const assignableUserOptions = useMemo(
    () => [
      { value: "", label: "None" },
      ...assignableUsers.map((user) => ({
        value: user.id,
        label: user.name?.trim() || user.email,
        description: `${user.email} - ${roleLabel(user.role)}`,
      })),
    ],
    [assignableUsers],
  );
  const assignmentWorkspaceOptions = useMemo(
    () => [
      { value: "", label: "All workspaces" },
      ...(tenantsQuery.data ?? []).map((workspace) => ({
        value: workspace.id,
        label: workspace.name,
        description: workspace.domain ?? workspace.id,
      })),
    ],
    [tenantsQuery.data],
  );
  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const togglePageAll = useCallback((checked: boolean, pageDeviceIds: string[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        pageDeviceIds.forEach((id) => next.add(id));
      } else {
        pageDeviceIds.forEach((id) => next.delete(id));
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const toggleSort = useCallback((key: DeviceTableSortKey) => {
    setPage(0);
    setFilters((f) => {
      if (f.sort === key) {
        return { ...f, order: f.order === "asc" ? "desc" : "asc" };
      }
      return { ...f, sort: key, order: DEVICE_TABLE_DEFAULT_SORT_ORDER[key] };
    });
  }, []);

  const resetFilters = useCallback(() => {
    setPage(0);
    setFilters({
      search: "",
      status: "",
      statusScope: "",
      protocol: "",
      locationValue: "",
      locationKind: "",
      simType: "",
      assignmentState: "",
      assignedUserId: "",
      warrantyScope: "",
      deviceCategoryId: "",
      alertPreset: "",
      alertRangeKey: "",
      alertStart: "",
      alertEnd: "",
      sort: null,
      order: "desc",
    });
    setLocationInput("");
    if (isSuperAdmin) {
    }
    if (location.search) {
      navigate(location.pathname, { replace: true });
    }
  }, [isSuperAdmin, location.pathname, location.search, navigate]);

  const openCreateLogger = useCallback(() => {
    if (!canCreateDevices) {
      setToast("You don't have permission to create data loggers.");
      return;
    }
    navigate("/devices/new");
  }, [canCreateDevices, navigate]);

  const onDeleteDevice = useCallback((d: DeviceDto) => {
    setConfirmDelete({ mode: "one", device: d });
  }, []);

  const onOpenAssignUsers = useCallback((d: DeviceDto) => {
    setAssignDialog({ devices: [d], mode: "single" });
    setAssignmentWorkspaceFilter(d.tenant_id ?? "");
    setAssignedUserIds(readAssignedUserIds(d).slice(0, 1));
  }, []);

  const onOpenBulkAssignUsers = useCallback(() => {
    if (!bulkAssignableSelection) {
      return;
    }
    setAssignDialog({ devices: selectedVisibleDevices, mode: "bulk" });
    setAssignmentWorkspaceFilter("");
    setAssignedUserIds([]);
  }, [bulkAssignableSelection, selectedVisibleDevices]);

  const closeAssignDialog = useCallback(() => {
    if (assignmentMut.isPending) {
      return;
    }
    setAssignDialog(null);
    setAssignmentWorkspaceFilter("");
    setAssignedUserIds([]);
  }, [assignmentMut.isPending]);

  const onDisableDevice = useCallback((d: DeviceDto) => {
    setDisableDialog(d);
  }, []);

  const onSaveAssignedUsers = useCallback(async () => {
    if (!assignDialog) {
      return;
    }
    for (const device of assignDialog.devices) {
      await assignmentMut.mutateAsync({
        deviceId: device.id,
        userIds: assignedUserIds.slice(0, 1),
        tenantId: assignmentWorkspaceFilter || null,
      });
    }
    setAssignDialog(null);
    setAssignmentWorkspaceFilter("");
    setAssignedUserIds([]);
    clearSelection();
  }, [assignDialog, assignedUserIds, assignmentMut, assignmentWorkspaceFilter, clearSelection]);

  const runDelete = useCallback(async () => {
    if (!confirmDelete) {
      return;
    }
    const expected = confirmDelete.mode === "bulk"
      ? `DELETE ${selectedIds.size}`
      : (confirmDelete.device?.device_uid ?? "");
    if (deleteConfirmText.trim() !== expected) {
      return;
    }
    if (confirmDelete.mode === "one" && confirmDelete.device) {
      await deleteMut.mutateAsync(confirmDelete.device.id);
    } else {
      const ids = [...selectedIds];
      await Promise.all(ids.map((id) => deleteMut.mutateAsync(id)));
      clearSelection();
    }
    setConfirmDelete(null);
    setDeleteConfirmText("");
  }, [confirmDelete, clearSelection, deleteConfirmText, deleteMut, selectedIds]);

  const runBackupDevice = useCallback(async (device: DeviceDto) => {
    const result = await backupDevice(device.id);
    setToast(result.message || "Backup queued. We will notify you when it is ready.");
  }, []);

  const exportDevices = useCallback(async () => {
    if (isExporting) {
      return;
    }

    setIsExporting(true);
    try {
      const exportedDevices: DeviceDto[] = [];
      let offset = 0;
      let nextOffset: number | null = 0;

      while (nextOffset !== null) {
        const result = await listDevicesPage({
          tenantId: isAllWorkspacesScope ? null : (effectiveWorkspaceFilter || null),
          workspaceScope: isAllWorkspacesScope ? "all" : null,
          limit: 200,
          offset,
          search: effectiveFilters.search || undefined,
          status: effectiveFilters.status || undefined,
          statusScope: effectiveFilters.statusScope || undefined,
          protocol: effectiveFilters.protocol || undefined,
          locationKind: effectiveFilters.locationKind || undefined,
          locationValue: effectiveFilters.locationValue || undefined,
          simType: effectiveFilters.simType || undefined,
          assignmentState: effectiveFilters.assignmentState || undefined,
          assignedUserId: effectiveFilters.assignedUserId || undefined,
          warrantyScope: effectiveFilters.warrantyScope || undefined,
          alertPreset: effectiveFilters.alertPreset || undefined,
          alertRangeKey: effectiveFilters.alertRangeKey || undefined,
          alertStart: effectiveFilters.alertStart || undefined,
          alertEnd: effectiveFilters.alertEnd || undefined,
          sort: effectiveFilters.sort,
          order: effectiveFilters.order,
        });
        exportedDevices.push(...result.items);
        nextOffset = result.next_offset;
        if (nextOffset === null || nextOffset <= offset) {
          break;
        }
        offset = nextOffset;
      }

      downloadDeviceCsv(exportedDevices);
      setToast(`${exportedDevices.length} logger${exportedDevices.length === 1 ? "" : "s"} exported as CSV.`);
    } catch {
      setToast("Unable to export data loggers. Please try again.");
    } finally {
      setIsExporting(false);
    }
  }, [effectiveFilters, effectiveWorkspaceFilter, isAllWorkspacesScope, isExporting]);

  const runBackupDelete = useCallback(async () => {
    if (!confirmBackupDelete) return;
    const result = await backupAndDeleteDevice(confirmBackupDelete.id);
    setConfirmBackupDelete(null);
    await listQuery.refetch();
    setToast(result.message || "Backup and delete queued. We will notify you when the backup is ready.");
  }, [confirmBackupDelete, listQuery]);

  const submitDisable = useCallback(
    async (mode: "ingestion" | "visualization" | null) => {
      if (!disableDialog) {
        return;
      }
      const meta = (disableDialog.metadata ?? {}) as JsonObject;
      const nextMetadata = { ...meta } as JsonObject;
      delete nextMetadata.disable_state;
      delete nextMetadata.disabled;
      delete nextMetadata.disabled_at;
      delete nextMetadata.disabled_mode;
      const bodyMetadata: JsonObject =
        mode === null
          ? nextMetadata
          : {
              ...nextMetadata,
              disable_state: {
                mode,
                disabled_at: new Date().toISOString(),
              },
            } as JsonObject;
      await patchMut.mutateAsync({
        deviceId: disableDialog.id,
        body: {
          metadata: bodyMetadata,
        },
      });
      setDisableDialog(null);
      await listQuery.refetch();
      setToast(
        mode === null
          ? "Device re-enabled."
          : mode === "ingestion"
            ? "Device ingestion disabled."
            : "Device visualization disabled.",
      );
    },
    [disableDialog, listQuery, patchMut],
  );

  const deleteConfirmationPhrase = useMemo(() => {
    if (!confirmDelete) {
      return "";
    }
    return confirmDelete.mode === "bulk" ? `DELETE ${selectedIds.size}` : (confirmDelete.device?.device_uid ?? "");
  }, [confirmDelete, selectedIds.size]);

  const deleteConfirmationMatches = confirmDelete !== null && deleteConfirmText.trim() === deleteConfirmationPhrase;

  const bulkDisable = useCallback(async () => {
    const targets = items.filter((d) => selectedIds.has(d.id));
    await Promise.all(
      targets.map((d) => {
        const meta = (d.metadata ?? {}) as JsonObject;
        const nextMetadata = { ...meta } as JsonObject;
        delete nextMetadata.disable_state;
        delete nextMetadata.disabled;
        delete nextMetadata.disabled_at;
        delete nextMetadata.disabled_mode;
        return patchMut.mutateAsync({
          deviceId: d.id,
          body: {
            metadata: {
              ...nextMetadata,
              disable_state: {
                mode: "ingestion",
                disabled_at: new Date().toISOString(),
              },
            },
          },
        });
      }),
    );
    clearSelection();
  }, [clearSelection, items, patchMut, selectedIds]);

  const pageLabel = useMemo(() => {
    if (total === 0) {
      return "0 results";
    }
    const start = page * pageSize + 1;
    const end = Math.min((page + 1) * pageSize, total);
    return `${start}-${end} of ${total}`;
  }, [page, pageSize, total]);

  useEffect(() => {
    if (page > 0 && items.length === 0 && !listQuery.isFetching) {
      setPage(Math.max(0, pageCount - 1));
    }
  }, [items.length, listQuery.isFetching, page, pageCount]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasPresetFilters = Boolean(
      params.get("protocol") ||
      params.get("status_scope") ||
      params.get("location_value") ||
      params.get("sim_type") ||
      params.get("assignment_state") ||
      params.get("warranty_scope") ||
      params.get("alert_preset") ||
      params.get("alert_range_key") ||
      params.get("alert_start") ||
      params.get("alert_end"),
    );
    setPage(0);
    setFilters((current) => ({
      ...current,
      status: params.get("status") ?? "",
      statusScope: (params.get("status_scope") as DeviceManagementListFilters["statusScope"]) ?? "",
      locationValue: params.get("location_value") ?? "",
      locationKind: (params.get("location_kind") as DeviceManagementListFilters["locationKind"]) ?? "",
      simType: (params.get("sim_type") as DeviceManagementListFilters["simType"]) ?? "",
      warrantyScope: (params.get("warranty_scope") as DeviceManagementListFilters["warrantyScope"]) ?? "",
      alertPreset: (params.get("alert_preset") as DeviceManagementListFilters["alertPreset"]) ?? "",
      alertRangeKey: (params.get("alert_range_key") as DeviceManagementListFilters["alertRangeKey"]) ?? "",
      alertStart: isoToLocalDateTimeInput(params.get("alert_start") ?? ""),
      alertEnd: isoToLocalDateTimeInput(params.get("alert_end") ?? ""),
    }));
    if (hasPresetFilters) {
      setShowAdvancedFilters(true);
    }
  }, [location.search]);

  useEffect(() => {
    setDeleteConfirmText("");
  }, [confirmDelete]);

  if (!effectiveWorkspaceFilter && !isAllWorkspacesScope) {
    return (
      <WorkspacePage className="pt-0">
        <EmptyState
          title="Authentication required"
          description="Sign in with a workspace-bound user to manage devices."
          action={
            <Link to="/login" className="btn btn-primary btn-sm !no-underline">
              Sign in
            </Link>
          }
        />
      </WorkspacePage>
    );
  }

  return (
    <WorkspacePage className="flex min-h-[calc(100vh-5rem)] max-w-[110rem] flex-col pt-0">
      <div className="flex flex-1 flex-col gap-ds-4">
        {/* <PageHeader
          kicker="Devices"
          title="Fleet registry"
          description="Search, filter, assign, disable, and maintain logger records from a denser CRM-style operations workspace."
          actions={
            <div className="flex flex-wrap gap-ds-2">
              <button type="button" onClick={openCreateLogger} className="btn btn-primary btn-sm">
                New logger
              </button>
            </div>
          }
        /> */}
        {selectedIds.size > 0 ? (
          <Card className="sticky top-ds-3 z-10 flex flex-wrap items-center justify-between gap-ds-3 border-primary/15 bg-card/95 p-ds-4 shadow-lg backdrop-blur">
            <div className="space-y-1">
              <p className="text-caption font-medium text-foreground">
                {selectedIds.size} logger{selectedIds.size === 1 ? "" : "s"} selected
              </p>
              <p className="text-micro text-muted">Bulk actions apply to the current selection set.</p>
            </div>
            <div className="flex flex-wrap gap-ds-2">
              {canManageCurrentScope && isSuperAdmin && bulkAssignableSelection ? (
                <Button type="button" variant="secondary" size="sm" onClick={onOpenBulkAssignUsers}>
                  {bulkAssignmentLabel}
                </Button>
              ) : null}
              {canManageCurrentScope ? (
                <Button type="button" variant="secondary" size="sm" onClick={() => void bulkDisable()}>
                  Disable selected
                </Button>
              ) : null}
              {canManageCurrentScope ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="!px-2 text-error"
                  onClick={() => setConfirmDelete({ mode: "bulk" })}
                  title="Delete selected"
                  aria-label="Delete selected"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              ) : null}
              <button type="button" className="btn btn-ghost btn-sm" onClick={clearSelection}>
                Clear
              </button>
            </div>
          </Card>
        ) : null}

        <ListSurface>
          <ListSurfaceHeader
            title="Fleet results"
            meta={
              <div className="flex items-center gap-ds-2">
                <ListCount total={total} noun={total === 1 ? "logger" : "loggers"} />
                {listQuery.isFetching && !listQuery.isLoading ? (
                  <span className="inline-flex items-center gap-ds-1">
                    <RefreshCw className="h-3 w-3 animate-spin" aria-hidden />
                    Refreshing
                  </span>
                ) : null}
              </div>
            }
            controls={
              <ListToolbar>
                <ListToolbarGroup grow>
                  <TableHeaderSearch
                    value={filters.search}
                    onChange={(value) => {
                      setPage(0);
                      setFilters((f) => ({ ...f, search: value }));
                    }}
                    placeholder="Search loggers"
                    className={cn(filters.search && "!border-[var(--accent-primary)] !bg-[var(--accent-primary-muted)]")}
                  />
                </ListToolbarGroup>
                {canCreateDevices ? (
                  <ToolbarIconButton icon={Plus} label="New logger" variant="primary" onClick={openCreateLogger} />
                ) : null}
                <ToolbarIconButton
                  icon={SlidersHorizontal}
                  label={showAdvancedFilters ? "Hide advanced filters" : "More filters"}
                  className={cn((showAdvancedFilters || hasAdvancedFilters) && "!border-primary/25 !text-primary")}
                  onClick={() => setShowAdvancedFilters((v) => !v)}
                />
                <ToolbarIconButton
                  icon={isExporting ? Loader2 : Download}
                  iconClassName={isExporting ? "animate-spin" : undefined}
                  label={isExporting ? "Exporting data loggers" : "Export CSV (Excel compatible)"}
                  onClick={() => void exportDevices()}
                  disabled={isExporting || listQuery.isLoading}
                />
              </ListToolbar>
            }
            extra={
              showAdvancedFilters ? (
                <div className="flex w-full flex-nowrap items-center gap-ds-2 overflow-x-auto py-0.5">
                  <ThemedSelect
                    value={filters.protocol}
                    options={PROTOCOL_OPTIONS}
                    ariaLabel="Protocol"
                    className="w-[8.5rem] shrink-0"
                    triggerClassName={filterTriggerClass(Boolean(filters.protocol))}
                    onChange={(nextValue) => {
                      setPage(0);
                      setFilters((f) => ({ ...f, protocol: nextValue }));
                    }}
                  />
                  <ThemedSelect
                    value={filters.simType}
                    options={SIM_TYPE_OPTIONS}
                    ariaLabel="SIM type"
                    className="w-[8.5rem] shrink-0"
                    triggerClassName={filterTriggerClass(Boolean(filters.simType))}
                    onChange={(nextValue) => {
                      setPage(0);
                      setFilters((f) => ({
                        ...f,
                        simType: nextValue as DeviceManagementListFilters["simType"],
                      }));
                    }}
                  />
                  <LocationFilterInput
                    value={locationInput}
                    selectedKind={filters.locationKind}
                    tenantId={isAllWorkspacesScope ? null : (effectiveWorkspaceFilter || null)}
                    workspaceScope={isAllWorkspacesScope ? "all" : null}
                    active={Boolean(filters.locationValue)}
                    onInputChange={(nextValue) => {
                      setLocationInput(nextValue);
                      if (!nextValue.trim()) {
                        setPage(0);
                        setFilters((f) => ({
                          ...f,
                          locationValue: "",
                          locationKind: "",
                        }));
                      } else if (filters.locationValue) {
                        setPage(0);
                        setFilters((f) => ({
                          ...f,
                          locationValue: "",
                          locationKind: "",
                        }));
                      }
                    }}
                    onSelect={(option) => {
                      setPage(0);
                      setLocationInput(option.label);
                      setFilters((f) => ({
                        ...f,
                        locationValue: option.label,
                        locationKind: option.kind,
                      }));
                    }}
                  />
                  <ThemedSelect
                    value={filters.status}
                    options={STATUS_OPTIONS}
                    ariaLabel="Operational state"
                    className="w-[8.5rem] shrink-0"
                    triggerClassName={filterTriggerClass(Boolean(filters.status))}
                    onChange={(nextValue) => {
                      setPage(0);
                      setFilters((f) => ({
                        ...f,
                        status: nextValue,
                        statusScope: "",
                      }));
                    }}
                  />
                  {isPlatformOperator ? (
                    <ThemedSelect
                      value={filters.deviceCategoryId}
                      options={[{ value: "", label: "Any category" }, ...(categoriesQuery.data ?? []).map((category) => ({ value: category.id, label: category.name }))]}
                      ariaLabel="Device category"
                      className="w-[9rem] shrink-0"
                      triggerClassName={filterTriggerClass(Boolean(filters.deviceCategoryId))}
                      onChange={(nextValue) => { setPage(0); setFilters((current) => ({ ...current, deviceCategoryId: nextValue })); }}
                    />
                  ) : null}
                  {isPlatformOperator ? (
                    <>
                      <ThemedSelect
                        value={filters.assignmentState}
                        options={ASSIGNMENT_OPTIONS}
                        ariaLabel="Assignment state"
                        className="w-[8.5rem] shrink-0"
                        triggerClassName={filterTriggerClass(Boolean(filters.assignmentState))}
                        onChange={(nextValue) => {
                          setPage(0);
                          setFilters((f) => ({
                            ...f,
                            assignmentState: nextValue as DeviceManagementListFilters["assignmentState"],
                            assignedUserId: nextValue === "assigned" ? f.assignedUserId : "",
                          }));
                          if (nextValue !== "assigned") setAssignedUserInput("");
                        }}
                      />
                      {filters.assignmentState === "assigned" ? (
                        <AssignedUserFilterInput
                          value={assignedUserInput}
                          selectedUserId={filters.assignedUserId}
                          tenantId={isAllWorkspacesScope ? null : (effectiveWorkspaceFilter || null)}
                          workspaceScope={isAllWorkspacesScope ? "all" : null}
                          onInputChange={(value) => {
                            setAssignedUserInput(value);
                            if (filters.assignedUserId) {
                              setPage(0);
                              setFilters((current) => ({ ...current, assignedUserId: "" }));
                            }
                          }}
                          onSelect={(user) => {
                            setPage(0);
                            setAssignedUserInput(user.name?.trim() || user.email || "Assigned user");
                            setFilters((current) => ({ ...current, assignmentState: "assigned", assignedUserId: user.id }));
                          }}
                        />
                      ) : null}
                    </>
                  ) : null}
                  <ThemedSelect
                    value={filters.warrantyScope}
                    options={WARRANTY_OPTIONS}
                    ariaLabel="Warranty scope"
                    className="w-[8.5rem] shrink-0"
                    triggerClassName={filterTriggerClass(Boolean(filters.warrantyScope))}
                    onChange={(nextValue) => {
                      setPage(0);
                      setFilters((f) => ({
                        ...f,
                        warrantyScope: nextValue as DeviceManagementListFilters["warrantyScope"],
                      }));
                    }}
                  />
                  <ThemedSelect
                    value={filters.alertPreset}
                    options={ALERT_PRESET_OPTIONS}
                    ariaLabel="Alert preset"
                    className="w-[9rem] shrink-0"
                    triggerClassName={filterTriggerClass(Boolean(filters.alertPreset))}
                    onChange={(nextValue) => {
                      setPage(0);
                      setFilters((f) => ({
                        ...f,
                        alertPreset: nextValue as DeviceManagementListFilters["alertPreset"],
                        alertRangeKey:
                          nextValue === "threshold_breached" ? f.alertRangeKey || "last_24h" : "",
                        alertStart: nextValue === "threshold_breached" ? f.alertStart : "",
                        alertEnd: nextValue === "threshold_breached" ? f.alertEnd : "",
                      }));
                    }}
                  />
                  {filters.alertPreset === "threshold_breached" ? (
                    <ThemedSelect
                      value={filters.alertRangeKey || "last_24h"}
                      options={ALERT_RANGE_OPTIONS}
                      ariaLabel="Alert range"
                      className="w-[8rem] shrink-0"
                      triggerClassName={filterTriggerClass(Boolean(filters.alertRangeKey))}
                      onChange={(nextValue) => {
                        setPage(0);
                        setFilters((f) => ({
                          ...f,
                          alertRangeKey: nextValue as DeviceManagementListFilters["alertRangeKey"],
                        }));
                      }}
                    />
                  ) : null}
                  {filters.alertPreset === "threshold_breached" && filters.alertRangeKey === "custom" ? (
                    <>
                      <input
                        type="datetime-local"
                        aria-label="Alert start"
                        className={cn(inputClass(), "h-9 w-[10.5rem] shrink-0", filters.alertStart && "!border-[var(--accent-primary)] !bg-[var(--accent-primary-muted)]")}
                        value={filters.alertStart}
                        onChange={(e) => {
                          setPage(0);
                          setFilters((f) => ({ ...f, alertStart: e.target.value }));
                        }}
                      />
                      <input
                        type="datetime-local"
                        aria-label="Alert end"
                        className={cn(inputClass(), "h-9 w-[10.5rem] shrink-0", filters.alertEnd && "!border-[var(--accent-primary)] !bg-[var(--accent-primary-muted)]")}
                        value={filters.alertEnd}
                        onChange={(e) => {
                          setPage(0);
                          setFilters((f) => ({ ...f, alertEnd: e.target.value }));
                        }}
                      />
                    </>
                  ) : null}
                  {hasAnyFilters ? (
                    <Button type="button" variant="secondary" size="sm" className="sticky right-0 shrink-0 !border-[var(--accent-primary)] !bg-[var(--accent-primary-muted)] !text-[var(--accent-primary)]" onClick={resetFilters}>
                      Clear all
                    </Button>
                  ) : null}
                </div>
              ) : null
            }
          />
          <ListSurfaceBody>
            {listQuery.isLoading ? (
              <div className="p-ds-4" aria-label="Loading fleet results">
                {Array.from({ length: 8 }, (_, index) => <SkeletonTableRow key={index} />)}
              </div>
            ) : listQuery.isError ? (
              <Alert className="m-ds-4" tone="error" title="Fleet results unavailable" description="Could not load devices. Adjust the filters or retry shortly." />
            ) : items.length === 0 ? (
              <div className="p-ds-6">
                <EmptyState
                  icon={Cpu}
                  title="No devices match"
                  description={canCreateDevices ? "Adjust filters or create a device to see it here." : "Adjust filters to find assigned data loggers."}
                  action={
                    canCreateDevices ? (
                      <Button type="button" size="sm" onClick={openCreateLogger}>
                        New logger
                      </Button>
                    ) : null
                  }
                />
              </div>
            ) : (
              <>
                <DeviceTable
                  devices={items}
                  selectedIds={selectedIds}
                  onToggleSelected={toggleSelected}
                  onTogglePageAll={togglePageAll}
                  onExternalDataAccess={canManageCurrentScope ? ((_) => undefined) : undefined}
                  onAssignUsers={canManageCurrentScope && isSuperAdmin ? onOpenAssignUsers : undefined}
                  onBackup={canManageCurrentScope ? runBackupDevice : undefined}
                  onBackupDelete={canManageCurrentScope ? setConfirmBackupDelete : undefined}
                  onDelete={canManageCurrentScope ? onDeleteDevice : undefined}
                  onDisable={canManageCurrentScope && canEditDevices ? (d) => void onDisableDevice(d) : undefined}
                  sort={filters.sort}
                  order={filters.order}
                  onSortChange={toggleSort}
                />
                <ListSurfaceFooter
                  left={
                    <label className="flex items-center gap-ds-2 md:justify-self-start">
                      <span className="text-caption text-muted">Rows per page</span>
                      <ThemedSelect
                        value={String(pageSize)}
                        ariaLabel="Rows per page"
                        className="w-[5.5rem]"
                        triggerClassName="h-10 border-border bg-card hover:border-primary/20"
                        options={DEVICE_MANAGEMENT_PAGE_SIZE_OPTIONS.map((option) => ({
                          value: String(option),
                          label: String(option),
                        }))}
                        onChange={(nextValue) => {
                          setPage(0);
                          setPageSize(Number(nextValue));
                        }}
                      />
                    </label>
                  }
                  right={
                    <>
                      <span className="text-caption text-muted">Fleet results {pageLabel}</span>
                      <div className="flex items-center gap-ds-2 md:justify-self-end">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm inline-flex items-center gap-ds-1"
                          disabled={page <= 0}
                          onClick={() => setPage((p) => Math.max(0, p - 1))}
                        >
                          <ChevronLeft className="h-4 w-4" aria-hidden />
                          Previous
                        </button>
                        <span className="text-caption tabular-nums text-muted">
                          Page {page + 1} / {pageCount}
                        </span>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm inline-flex items-center gap-ds-1"
                          disabled={page + 1 >= pageCount}
                          onClick={() => setPage((p) => p + 1)}
                        >
                          Next
                          <ChevronRight className="h-4 w-4" aria-hidden />
                        </button>
                      </div>
                    </>
                  }
                />
              </>
            )}
          </ListSurfaceBody>
        </ListSurface>
      </div>
      {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title={confirmDelete?.mode === "bulk" ? "Delete selected devices?" : "Delete device?"}
        description={
          confirmDelete?.mode === "bulk"
            ? `This will permanently remove ${selectedIds.size} devices and their related telemetry, alerts, connections, reports, commands, and logs.`
            : confirmDelete?.device
              ? `Permanently remove ${confirmDelete.device.name ?? confirmDelete.device.device_uid} and its related telemetry, alerts, connections, reports, commands, and logs?`
              : undefined
        }
        footer={
          <div className="flex justify-end gap-ds-2">
            <Button type="button" variant="secondary" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={() => void runDelete()}
              disabled={deleteMut.isPending || !deleteConfirmationMatches}
            >
              Delete
            </Button>
          </div>
        }
      >
        <div className="space-y-ds-3">
          <div className="rounded-lg border border-error/25 bg-error/5 px-ds-3 py-ds-2 text-caption text-error">
            This action cannot be undone.
          </div>
          <label className="block space-y-ds-1">
            <span className="text-micro font-semibold uppercase tracking-wider text-muted">
              Type <span className="font-bold text-foreground">{deleteConfirmationPhrase}</span> to confirm
            </span>
            <input
              className={inputClass()}
              value={deleteConfirmText}
              onChange={(event) => setDeleteConfirmText(event.target.value)}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
          </label>
        </div>
      </Modal>

      <Modal
        open={confirmBackupDelete !== null}
        onClose={() => setConfirmBackupDelete(null)}
        title="Backup and delete device?"
        description={
          confirmBackupDelete
            ? `Generate a backup ZIP for ${confirmBackupDelete.name ?? confirmBackupDelete.device_uid}, then permanently delete the data logger when the backup is ready.`
            : undefined
        }
        footer={
          <div className="flex justify-end gap-ds-2">
            <Button type="button" variant="secondary" onClick={() => setConfirmBackupDelete(null)}>
              Cancel
            </Button>
            <Button type="button" variant="danger" onClick={() => void runBackupDelete()}>
              Backup & delete
            </Button>
          </div>
        }
      >
        <div className="rounded-lg border border-warning/25 bg-warning/5 px-ds-3 py-ds-2 text-caption text-warning">
          The backup is generated first. Deletion runs only after the ZIP is prepared.
        </div>
      </Modal>

      <DeviceDisableModal
        device={disableDialog}
        currentState={readDeviceDisableState(disableDialog)}
        open={disableDialog !== null}
        pending={patchMut.isPending}
        onClose={() => setDisableDialog(null)}
        onConfirm={(mode) => submitDisable(mode)}
      />

      <Modal
        open={assignDialog !== null}
        onClose={closeAssignDialog}
        title={
          assignDialog
            ? assignDialog.mode === "bulk"
              ? `Assign user to ${assignDialog.devices.length} selected loggers`
              : `Assign user to ${assignDialog.devices[0]?.name?.trim() || assignDialog.devices[0]?.device_uid}`
            : "Assign user"
        }
        description={
          assignDialog?.mode === "bulk"
            ? "All selected data loggers will be assigned to the same admin. Use workspace as an optional user filter."
            : "A data logger can be assigned to one user. Use workspace as an optional user filter."
        }
        size="lg"
        footer={
          <div className="flex justify-end gap-ds-2">
            <Button type="button" variant="secondary" onClick={closeAssignDialog} disabled={assignmentMut.isPending}>
              Cancel
            </Button>
            <Button type="button" onClick={() => void onSaveAssignedUsers()} disabled={assignmentMut.isPending}>
              {assignmentMut.isPending ? "Saving..." : "Save assignment"}
            </Button>
          </div>
        }
      >
        <div className="space-y-ds-4">
          {assignDialog?.mode === "bulk" ? (
            <div className="rounded-lg border border-border/70 bg-muted-bg/20 px-ds-3 py-ds-2 text-caption text-muted">
              {assignDialog.devices.length} unassigned logger{assignDialog.devices.length === 1 ? "" : "s"} will be updated together.
            </div>
          ) : null}
          <label className="block space-y-ds-1">
            <span className="text-micro font-semibold uppercase tracking-wider text-muted">Workspace filter</span>
            <ThemedSelect
              value={assignmentWorkspaceFilter}
              options={assignmentWorkspaceOptions}
              ariaLabel="Workspace filter for users"
              triggerClassName="h-11 border-border bg-card hover:border-primary/20"
              searchable
              searchPlaceholder="Search workspaces"
              onChange={(nextValue) => {
                setAssignmentWorkspaceFilter(nextValue);
                setAssignedUserIds([]);
              }}
            />
          </label>

          {usersQuery.isLoading ? (
            <p className="text-caption text-muted">Loading users...</p>
          ) : usersQuery.isError ? (
            <p className="text-caption text-error">
              {usersQuery.error instanceof Error ? usersQuery.error.message : "Could not load users."}
            </p>
          ) : (
            <label className="block space-y-ds-1">
              <span className="text-micro font-semibold uppercase tracking-wider text-muted">Assigned user</span>
              <ThemedSelect
                value={assignedUserId}
                options={assignableUserOptions}
                ariaLabel="Assigned user"
                placeholder="None"
                triggerClassName="h-11 border-border bg-card hover:border-primary/20"
                menuClassName="max-w-[min(36rem,calc(100vw-2rem))]"
                searchable
                searchPlaceholder="Search users"
                onChange={(nextValue) => setAssignedUserIds(nextValue ? [nextValue] : [])}
              />
              {assignableUsers.length === 0 ? (
                <span className="block text-caption text-muted">No assignable users are available for this filter.</span>
              ) : null}
            </label>
          )}
        </div>
      </Modal>
    </WorkspacePage>
  );
}
